import { isSome } from '@mbfab/shared'
import type { ReactNode } from 'react'

import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/lib/utils'

// 一覧テーブルのカード枠（発注照合テーブルの実装を切り出した共通部品）。
// ツールバー・バナー・テーブル本体・ページネーションのスロットだけを提供し、
// 中身のロジック・マークアップは呼び出し側に残す
export function DataTableShell({
  toolbar,
  banners,
  loading,
  showOverlay,
  topPagination,
  pagination,
  children,
  testId,
}: {
  toolbar?: ReactNode
  banners?: ReactNode
  // aria-busy・inert の対象かどうか（初回ロードのskeleton・再取得のオーバーレイのどちらでも true）。
  // スピナーの見た目（showOverlay）とは切り分ける（初回ロードは skeleton 側で表現するため、
  // ここではオーバーレイを重ねない。呼び出し元は data-table.tsx の loading をそのまま渡す）
  loading?: boolean
  // 読み込み中スピナーのオーバーレイを重ねて表示するか（再取得中＝既存行がある場合のみ true）
  showOverlay?: boolean
  // ツールバー帯の右端に置くページネーション（行数の多い一覧で、下端までスクロールせずにページを送るため）
  topPagination?: ReactNode
  pagination?: ReactNode
  children: ReactNode
  testId?: string
}) {
  return (
    <div
      className="rounded-xl border border-border bg-card shadow-xs [container-type:inline-size]"
      data-testid={testId}
      data-loading={loading === true ? true : undefined}
      aria-busy={loading === true}
    >
      {/* バナー（エラーパネル等）は inert / 半透明の対象外にする。読み込み中でも文言を読み・
          コピーできる必要があるため（2026/08 レビューFB） */}
      {banners}
      {/* 読み込み中はスピナーのオーバーレイを「重ねる」形にする。高さ・幅を変えずに再取得を伝えるため
          （ツールバー帯へ文言を追加すると、出現・消失のたびにレイアウトが動いてカクついて見える。
          2026/08 FBで方針化）。relative の対象をツールバー・上部ページャ・テーブル本体・下部ページャ
          まで広げているのは、再取得中に「先行リクエストの表示中に次のページャ操作ができてしまう」
          退行を防ぐため（旧実装は DataTable 全体を inert していたのを、共通化時に本体だけに縮めてしまい
          発覚した。2026/08 レビューFB） */}
      <div className="relative">
        <div
          className={cn(
            'transition-opacity',
            // ページャまで含めて薄くする方が「読み込み中である」ことが伝わりやすいため、
            // バナーとは違いここは opacity を掛ける対象に含める（2026/08 レビューFBで明示）
            loading === true && 'pointer-events-none opacity-60 select-none',
          )}
          // aria-hidden は role="status" の告知テキストと二重になるため付けない。inert だけで
          // 支援技術からも除外される（inert は aria-hidden を包含する。2026/08 レビューFB）
          inert={loading === true ? true : undefined}
        >
          {/* toolbar が無いテーブル（items-table・CSV取込結果等の小テーブル）まで帯を常置すると、
              中身が空の min-h-14 の帯＋下線だけが視覚ノイズとして残るため、内容があるときだけ描画する。
              選択時ツールバー・列表示メニューを持つ一覧4画面はどちらか常に内容を持つため見た目は変わらない */}
          {/* 帯の行間（gap-y-2）は、下の toolbar ラッパーと同じ値にする。片方だけ gap-3 にすると、
              同じ画面でも「1ページに収まるとき（上部ページャ無し）だけ行間が広い」というブレになる */}
          {(isSome(toolbar) || isSome(topPagination)) && (
            <div className="flex min-h-14 flex-wrap items-center gap-x-3 gap-y-2 border-b border-border px-4 py-2.5">
              {/* 上部ページャが無いテーブル（1ページに収まる一覧・小テーブル）は、toolbar を帯の直接の子として
                  描画する。ラッパーを1枚挟んでも見た目は同じだが、包む意味がここには無い
                  （ラッパーは「ページャと横並びにしたときに残り幅で折り返す」ためのもの） */}
              {isSome(topPagination) ? (
                <>
                  {/* toolbar 側（選択件数＋一括アクション）は残り幅の中で折り返す。幅が足りないときは
                      アクション群だけが 2 行目に回り、ページャは 1 行目の右端に残る。
                      折り返し位置は中身の実寸で決まるので、画面ごとにボタン数が違っても
                      閾値を決め打ちせずに済む（2026/08/31 レイアウト崩れFB） */}
                  {isSome(toolbar) && (
                    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-2">{toolbar}</div>
                  )}
                  {/* self-start で 1 行目に固定する。既定の items-center のままだと、toolbar が 2 行に
                      なったときページャだけが 2 行ぶんの中央に浮き、どちらの行とも揃わない。
                      min-h-9 は帯 1 行ぶんの高さ（min-h-14 − py-2.5×2 = 36px）で、
                      SelectionActionBar 側の行の高さと合わせてある */}
                  <div className="ml-auto flex min-h-9 shrink-0 items-center self-start">{topPagination}</div>
                </>
              ) : (
                toolbar
              )}
            </div>
          )}
          <div className="overflow-x-auto [container-type:inline-size]">{children}</div>
          {isSome(pagination) && <div className="border-t border-border px-4 py-3">{pagination}</div>}
        </div>
        {/* 読み込み中の告知は role="status" の sr-only テキスト1つに一本化する（skeleton・オーバーレイの
            どちらでも出す）。オーバーレイのビジュアル（Spinner）は装飾として aria-hidden にし、
            role="status" の入れ子（二重読み上げ）を避ける（2026/08 レビューFB） */}
        {loading === true && (
          <span role="status" className="sr-only">
            読み込み中
          </span>
        )}
        {showOverlay === true && (
          <div
            className="pointer-events-none absolute inset-0 z-10 bg-background/60"
            data-testid={`${testId}-loading`}
            aria-hidden="true"
          >
            {/* sticky + 画面いっぱいの高さで、テーブルが縦に長くてもスピナーは
                画面に見えている範囲の中央に来る（テーブル自体が低ければテーブル中央になる） */}
            <div className="sticky top-0 flex h-[100dvh] max-h-full items-center justify-center">
              <Spinner className="size-6 text-muted-foreground" aria-hidden />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
